import { getDirector, truncateUntilPeriod } from '@lifedot/utils'
import { useShortCodeDataCemeteries } from '@/hooks/use-short-code-data/use-short-code-data'
import Image from 'next/image'
import Link from 'next/link'
import { FC } from 'react'
import { router } from '@lifedot/router'
import { makeLabels } from '@lifedot/utils'
import { Label } from '@lifedot/atoms/Label'
import { css } from '@emotion/react'
import { List, ListItem } from '@lifedot/atoms/List'
import CheckIcon from '@material-ui/icons/Check'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'
import { mq } from '@lifedot/styles/mediaQuery'

const styles = {
  recommend: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: {
      marginBottom: 0
    },
    [mq('sp')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  }),
  list: css({
    li: {
      marginTop: 4
    }
  }),
  icon: css({
    color: palette.main
  }),
  commentWrap: css({
    width: '50%',
    flexShrink: 0,
    marginLeft: 12,
    [mq('sp')]: {
      width: '100%',
      marginLeft: 0,
      marginTop: 8
    }
  }),
  comment: css({
    border: `1px solid ${palette.gray2}`,
    borderRadius: 4,
    padding: 12,
    backgroundColor: palette.white
  }),
  directorImg: css({
    display: 'inline-block',
    width: 48,
    borderRadius: '50%',
    marginRight: 4
  }),
  directorName: css({
    lineHeight: 3,
    fontWeight: 'bold',
    display: 'inline-block',
    verticalAlign: 'top'
  }),
  more: css({
    marginLeft: 4
  }),
  labels: css({
    marginTop: 8,
    '> *': {
      marginRight: 4
    }
  })
}

interface RecommendPointsProps {
  cemeteryId: number
}

export const RecommendPoints: FC<RecommendPointsProps> = ({ cemeteryId }) => {
  const [cemetery] = useShortCodeDataCemeteries([cemeteryId])
  if (!cemetery) return null
  const {
    descriptions,
    director_comment: directorComment,
    features,
    facilities
  } = cemetery
  const { iconImagePath: directorImagePath, name: directorName } =
    getDirector(cemeteryId)

  const labels = makeLabels([...features, ...facilities])

  return (
    <>
      <div css={styles.recommend}>
        <div css={styles.list}>
          {descriptions.length > 0 && (
            <List icon={<CheckIcon css={styles.icon} />}>
              {descriptions.map(({ subject }) => (
                <ListItem key={subject}>
                  <b css={typography.textL}>{subject}</b>
                </ListItem>
              ))}
            </List>
          )}
        </div>
        <div css={styles.commentWrap}>
          <figure css={styles.directorImg}>
            <Image
              src={directorImagePath}
              width={120}
              height={120}
              alt="ライフドット編集部の画像"
            />
          </figure>
          <p css={styles.directorName}>{directorName}のコメント</p>
          <p css={[typography.textS, styles.comment]}>
            {truncateUntilPeriod(directorComment ?? '')}…
            <Link href={router.cemetery(cemeteryId)} passHref prefetch={false}>
              <a css={styles.more}>続きを読む</a>
            </Link>
          </p>
        </div>
      </div>
      <div css={styles.labels}>
        {labels.map(({ name, code }) => (
          <Label key={code} emphasis>
            {name}
          </Label>
        ))}
      </div>
    </>
  )
}
